"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";

import { findMediaIndexByUrl, type MediaItem } from "@/lib/experience-media";
import { idlePrefetchLargeAll, prewarmGalleryOpen } from "@/lib/image-prefetch";

// Lazy-load the full-screen lightbox — none of its JS (filmstrip, keyboard
// nav, swipe) ships until the traveler actually opens the gallery, keeping
// the storefront's sub-1s / Lighthouse-95 budget intact.
const MediaLightbox = dynamic(
  () => import("./media-lightbox.client").then((m) => m.MediaLightbox),
  { ssr: false },
);

interface MediaGalleryContextValue {
  media: MediaItem[];
  openByIndex: (index: number) => void;
  openByUrl: (url: string) => void;
  /** Warm the first image + filmstrip thumbs so the lightbox opens already
   *  painted. Call on hover/focus/pointerdown of any gallery affordance. */
  prewarm: () => void;
}

const MediaGalleryContext = createContext<MediaGalleryContextValue | null>(
  null,
);

export function useMediaGallery(): MediaGalleryContextValue {
  const ctx = useContext(MediaGalleryContext);
  if (!ctx) {
    throw new Error(
      "useMediaGallery must be used inside <ExperienceMediaGalleryProvider>",
    );
  }
  return ctx;
}

/**
 * A one-item pool for a photo the collected media does not contain.
 *
 * Opening it alone is the only answer that stays truthful: the traveler clicked
 * THIS image, so this image is what opens. `openByUrl` used to fall back to
 * index 0, which is the reported bug wearing a different hat — normalising the
 * lookup key in `photoKey` closed the common cause (relative vs absolute
 * spellings of one URL), but any remaining miss still silently opened the first
 * gallery photo as though it were the one clicked.
 *
 * A pool can legitimately not contain a tile: a poster frame, or an image added
 * to the DOM after collection.
 */
function loneMediaItem(url: string, title: string): MediaItem {
  // `alt` carries the tour title, the same fallback `collectExperienceMedia`
  // gives every pooled photo (`img.alt || data.name`). An empty string here
  // would announce the ONLY element in the lightbox as decorative — i.e. as
  // nothing at all — to a screen reader.
  //
  // `sectionLabel` is empty on purpose and is the honest answer: this photo is
  // outside the collected pool, so there is no section it can claim to be from.
  return {
    id: `lone:${url}`,
    url,
    alt: title,
    section: "gallery",
    sectionLabel: "",
  };
}

export function ExperienceMediaGalleryProvider({
  media,
  title,
  children,
}: {
  media: MediaItem[];
  title: string;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const [loneItem, setLoneItem] = useState<MediaItem | null>(null);

  const openByIndex = useCallback(
    (i: number) => {
      if (media.length === 0) return;
      setLoneItem(null);
      setIndex(Math.max(0, Math.min(media.length - 1, i)));
      setIsOpen(true);
    },
    [media.length],
  );

  const openByUrl = useCallback(
    (url: string) => {
      const idx = media.length > 0 ? findMediaIndexByUrl(media, url) : -1;
      setLoneItem(idx >= 0 ? null : loneMediaItem(url, title));
      setIndex(idx >= 0 ? idx : 0);
      setIsOpen(true);
    },
    [media, title],
  );

  // Hover/focus intent on any gallery affordance warms the lightbox. The first
  // image + filmstrip thumbs warm every time (cheap); the FULL set of large
  // variants warms once in the background so that, by the time the traveler
  // opens the lightbox and taps prev/next, every image is already decoded in
  // cache — iteration is seamless with no loading state. Intent-gated (only
  // after the user shows interest) and connection-gated inside
  // `idlePrefetchLargeAll`, so visitors who never touch the gallery pay nothing.
  const fullWarmStarted = useRef(false);
  const prewarm = useCallback(() => {
    const urls = media.filter((m) => m.kind !== "video").map((m) => m.url);
    prewarmGalleryOpen(urls);
    if (!fullWarmStarted.current && urls.length > 0) {
      fullWarmStarted.current = true;
      idlePrefetchLargeAll(urls);
    }
  }, [media]);

  const value = useMemo<MediaGalleryContextValue>(
    () => ({ media, openByIndex, openByUrl, prewarm }),
    [media, openByIndex, openByUrl, prewarm],
  );

  return (
    <MediaGalleryContext.Provider value={value}>
      {children}
      {isOpen ? (
        <MediaLightbox
          media={loneItem ? [loneItem] : media}
          index={index}
          title={title}
          onIndexChange={setIndex}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </MediaGalleryContext.Provider>
  );
}
